import { useCallback, useEffect, useState } from 'react';
import { recorder } from '../../app/recorder';
import type { RecorderView } from '../../app/recorder';

/**
 * The recorder's state, for whichever surface is showing (build unit U-11).
 *
 * ── Why a subscription to a singleton, and not React state ──────────────────
 *
 * §5.6's rule is that *"recording active (FLAG-1, AUD-2, LIVE-4)"* is true **in any in-match state**
 * and must have a surface in every one of them. The recording itself outlives every one of those
 * surfaces: a voice note started on the locked board is still running after the user navigates to
 * Discovery, and AUD-3 lists `pagehide` and a `Match.state` transition among its finalize triggers,
 * neither of which any component is mounted for.
 *
 * So the recording lives in `app/recorder.ts` — one microphone, one indicator, one exit — and this
 * hook is a **read** of it. A `useState` holding the recording would make the recorder's lifetime a
 * component's lifetime, which is the same defect §2.6 names for the wake lock (*"bound to
 * `Match.state`, never to a mounted screen"*) and §5.6 names for the undo window.
 *
 * ── Two subscribers, deliberately ───────────────────────────────────────────
 *
 * The Flag bar reads it for FLAG-1's *"recording state is visible on the bar"*, and each capture
 * surface reads it for LIVE-4's *"persistent max-contrast recording border"* on the tile or row the
 * note belongs to. Both read the same value from the same place, so the bar and the tile cannot
 * disagree about whether something is being recorded.
 */
export function useRecorderView(): RecorderView {
  const [view, setView] = useState<RecorderView>(() => recorder.view());

  useEffect(() => {
    // Re-read on mount before subscribing: a recording may have started, or finalized, between the
    // initial `useState` and this effect — a surface that only listened would render the stale one
    // until the next change, which for a finalize is forever.
    setView(recorder.view());
    return recorder.subscribe(setView);
  }, []);

  return view;
}

/**
 * LIVE-4 / §5.6 — whether *this* hypothesis is the one being recorded right now.
 *
 * A predicate rather than a set, for the reason `BoardTile.undoOpen` is a prop rather than a
 * collection: there is at most one recording, and an intermediate set is a thing that can go stale
 * between the recorder and the tile.
 */
export function useRecordingBorder(view: RecorderView): (hypothesisId: string) => boolean {
  return useCallback(
    // No `silent` check: `view.recording` is published only for a real `ActiveRecording`, so a
    // FLAG-4 silent flag leaves it null and no tile can border. Re-checking `silent` here would be
    // a second guard over an unreachable case, which reads as though the case were reachable.
    //
    // **The subject arm is the guard, and it is stronger than the old null check.** `hypothesisId`
    // lives on the `voiceNote` arm alone (U-13c), so a flag and HT-8's halftime note are both
    // *structurally* unable to border a tile — where a nullable `hypothesisId` would have left
    // "does a halftime note border something?" as a question this predicate had to remember to ask.
    (hypothesisId: string) =>
      view.recording?.subject.kind === 'voiceNote' &&
      view.recording.subject.hypothesisId === hypothesisId,
    [view.recording],
  );
}
